import { useState } from "react";
import {
  getTaskMasters,
  postTaskMaster,
  updateTaskMaster,
  deleteTaskMaster,
  getSubTasks,
  postSubTask,
  updateSubTask,
  deleteSubTask,
} from "../../../Services/taskMasterService";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../constants";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

export const useTaskMaster = () => {
  const [taskMasters, setTaskMasters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    project_type: "",
    project_category: "",
    search: "",
  });

  // Main Tasks
  const fetchTaskMasters = async () => {
    setLoading(true);
    try {
      const res = await getTaskMasters({ ...filters, limit: 0, offset: 0 });
      if (res.status === 200) {
        setTaskMasters(res.data);
        setError(null);
      } else {
        toast.error(TOAST_MESSAGES.TASK_MASTER_FETCH_FAILED);
      }
    } catch (err) {
      setError(TOAST_MESSAGES.TASK_MASTER_FETCH_ERROR);
      toast.error(TOAST_MESSAGES.TASK_MASTER_FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const createTaskMaster = async (payload) => {
    try {
      setLoading(true);
      const res = await postTaskMaster(payload);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.CREATE_TASK_MASTER_SUCCESS);
        return true;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.CREATE_TASK_MASTER_FAILED;
        toast.error(msg);
        return false;
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.CREATE_TASK_MASTER_ERROR;
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const editTaskMaster = async (payload) => {
    try {
      setLoading(true);
      const res = await updateTaskMaster(payload);
      if (res.status === 200 || res.status === 204) {
        toast.success(TOAST_MESSAGES.UPDATE_TASK_MASTER_SUCCESS);
        fetchTaskMasters();
        return true;
      } else {
        toast.error(TOAST_MESSAGES.UPDATE_TASK_MASTER_FAILED);
        return false;
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.UPDATE_TASK_MASTER_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteTaskMasterById = async (id) => {
    try {
      setLoading(true);
      const res = await deleteTaskMaster(id);
      if (res.status === 204) {
        toast.success(TOAST_MESSAGES.DELETE_TASK_MASTER_SUCCESS);
        fetchTaskMasters();
        return true;
      } else {
        toast.error(TOAST_MESSAGES.DELETE_TASK_MASTER_FAILED);
        return false;
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.DELETE_TASK_MASTER_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Sub-tasks
  const fetchSubTasks = async (masterTaskId) => {
    if (!masterTaskId) return [];
    try {
      const res = await getSubTasks(masterTaskId);
      if (res.status === 200) {
        return res.data?.results || [];
      } else {
        toast.error(TOAST_MESSAGES.SUB_TASK_FETCH_FAILED);
        return [];
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.SUB_TASK_FETCH_ERROR);
      return [];
    }
  };

  const createSubTask = async (payload) => {
    try {
      setLoading(true);
      const res = await postSubTask(payload.super_task, payload);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.CREATE_SUB_TASK_SUCCESS);
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.CREATE_SUB_TASK_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.CREATE_SUB_TASK_ERROR;
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const editSubTask = async (masterId, subTaskId, payload) => {
    try {
      setLoading(true);
      const res = await updateSubTask(masterId, subTaskId, payload);
      if (res.status === 200 || res.status === 204) {
        toast.success(TOAST_MESSAGES.UPDATE_SUB_TASK_SUCCESS);
        return res.data;
      } else {
        toast.error(TOAST_MESSAGES.UPDATE_SUB_TASK_FAILED);
        return null;
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.UPDATE_SUB_TASK_ERROR);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteSubTaskById = async (masterId, subTaskId) => {
    try {
      setLoading(true);
      const res = await deleteSubTask(masterId, subTaskId);
      if (res.status === 204) {
        toast.success(TOAST_MESSAGES.DELETE_SUB_TASK_SUCCESS);
        return res.data;
      } else {
        toast.error(TOAST_MESSAGES.DELETE_SUB_TASK_FAILED);
        return null;
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.DELETE_SUB_TASK_ERROR);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    taskMasters,
    setTaskMasters,
    filters,
    setFilters,
    fetchTaskMasters,
    createTaskMaster,
    editTaskMaster,
    deleteTaskMasterById,
    fetchSubTasks,
    createSubTask,
    editSubTask,
    deleteSubTaskById,
  };
};